import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone'
import {type BaseSyntheticEvent, type FC, type ReactElement, useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import BasicInput from '@shared/components/BasicInput'
import {Box} from '@mui/material'

type SimpleSearchProps = {
  label?: string
  caseSensitive?: boolean
  size?: 'small' | 'medium' | 'large'
  dataCy?: string
  title: string
}

const SimpleSearchWithSearchParams: FC<SimpleSearchProps> = ({
  label,
  caseSensitive = false,
  size = 'medium',
  dataCy,
  title,
}): ReactElement => {
  const [t] = useCustomTranslation('common')
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get(title) || '')
  const debounceTime = 500

  const urlSearchValue = searchInput
    ? caseSensitive
      ? searchInput
      : searchInput.toLowerCase()
    : ''

  useEffect(() => {
    // Nothing to write: the URL already says what this input says. Without this
    // guard the effect fires on MOUNT too and rewrites the query 500ms later from
    // a render-time snapshot — React Router hands the updater the params of the
    // render that created the setter, not the live ones. Any param another
    // component sets inside that window is silently reverted; on Batch Tracing
    // that meant clicking a page tab within 500ms of the search mounting bounced
    // the user straight back to the previous tab.
    if (urlSearchValue === (searchParams.get(title) ?? '')) return undefined

    const handler = setTimeout(() => {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams)
          if (urlSearchValue) {
            newParams.set(title, urlSearchValue)
          } else {
            newParams.delete(title)
          }
          return newParams
        },
        {replace: true},
      )
    }, debounceTime)

    return () => clearTimeout(handler) // Cleanup on re-renders
    // searchParams is read for the comparison only; adding it as a dep would rearm
    // the debounce on every unrelated param change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchValue, title, debounceTime, setSearchParams])

  const handleSearchTermChange = (event: BaseSyntheticEvent): void => {
    const searchTerm = event.target.value
    setSearchInput(searchTerm)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <BasicInput
        data-testid="mbai-simple-search-component"
        id="mbai-main-search-component"
        data-cy={dataCy}
        InputProps={{
          startAdornment: (
            <SearchTwoToneIcon
              sx={{
                fontSize: '1.25rem',
                color: 'text.disabled',
              }}
            />
          ),
        }}
        onChange={handleSearchTermChange}
        value={searchInput}
        label={label || t('search')}
        displayLabel={false}
        mbaiSize={size}
        autoFocus
        onKeyDown={(event: BaseSyntheticEvent) => {
          event.stopPropagation()
        }}
      />
    </Box>
  )
}

export default SimpleSearchWithSearchParams
